import React from 'react';

import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { useChallengeData } from '../../../../../contexts/storage-contexts/Challenge';
import { ChallengeDots } from '../ChallengeDots';
import { ProgressBar } from '../../../../../components-shared/ProgressBar';
import { useSettings } from '@wix/tpa-settings/react';
import challengeSettings from '../../../settingsParams';
import { useUser } from '../../../../../contexts/User/UserContext';
import { isSelfPaced } from '../../../../../selectors/isSelfPaced';
import { FCWithChildren } from '@wix/challenges-web-library';
import { dataHooks } from '@wix/data-hooks';
import { isUserCompleted } from '../../../../../contexts/User/helpers/userTypeHandlers';
import { getState } from '../../../../../selectors/participant';
import { isProgramProgressReachedEnd } from '../../../../../selectors/participant/isProgramProgressReachedEnd';
import { getProgress } from '../../../../../selectors/participant/getProgress';
import { useCSSPBEnabled } from '../../../../../hooks/useCSSPBStyle';
import { classes, st } from './ChallengeSummary.st.css';
import { useSidebarLayoutBase } from '../../views/SidebarLayout/contexts/SidebarLayoutBase/SidebarLayoutBaseContext';
import { CollapseSidebarButton } from '../CollapseSidebarButton/CollapseSidebarButton';

export const summaryDataHooks = dataHooks<{
  main;
  title;
  progressBar;
}>('summary');

export interface ISummary {}

export const ChallengeSummary: FCWithChildren<ISummary> = () => {
  const { isSidebarCollapsed } = useSidebarLayoutBase();
  const { t } = useTranslation();
  const { isMobile, isRTL } = useEnvironment();
  const {
    challengeData: { challenge },
  } = useChallengeData();
  const settings = useSettings();
  const { participant, participantV3 } = useUser();
  const isSPC = isSelfPaced(challenge);
  const participantOrV3 = participantV3 || participant;
  const resolvedValue = getProgress(participantOrV3);
  const TitleTag = settings.get(challengeSettings.programTitleTag) || 'h1';
  const isFinished =
    isUserCompleted(getState(participantOrV3)) ||
    isProgramProgressReachedEnd(challenge, participantOrV3);
  const cssPBEnabled = useCSSPBEnabled({
    includeParticipantPageAlignmentCheck: true,
  });

  if (isSidebarCollapsed) {
    return <CollapseSidebarButton />;
  }

  return (
    <div
      className={st(classes.root, {
        align: !cssPBEnabled
          ? settings.get(challengeSettings.sidebarLayoutTextAlignment)
          : null,
        withCSSAlignment: cssPBEnabled,
        rtl: isRTL,
        collapsed: isSidebarCollapsed,
        mobile: isMobile,
      })}
      data-hook={summaryDataHooks.main()}
      dir="auto"
    >
      <div className={classes.header}>
        <div className={classes.titleColumn}>
          {isFinished ? (
            <p className={classes.completed}>{t('challenge.page.completed')}</p>
          ) : null}

          <TitleTag
            className={classes.title}
            data-hook={summaryDataHooks.title()}
            dir="auto"
          >
            {(challenge as any)?.shouldTranslateTitle
              ? t(challenge.settings.description.title)
              : challenge.settings.description.title}
          </TitleTag>
        </div>

        <div className={classes.dotsMenu}>
          {!isSidebarCollapsed && <ChallengeDots />}
          <CollapseSidebarButton />
        </div>
      </div>

      {isSPC ? (
        <ProgressBar
          className={classes.progress}
          dataHook={summaryDataHooks.progressBar()}
          value={resolvedValue}
        />
      ) : null}
    </div>
  );
};

ChallengeSummary.displayName = 'ChallengeSummary';
